import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  getFieldConfigurations,
  getActiveApplicationTypes,
  getActivePriorities,
  getActiveStatuses,
  FieldConfiguration,
  ApplicationType,
  ApplicationPriority,
  ApplicationStatus
} from '../services/formConfigService';

/**
 * Form Configuration Context
 * Provides global access to form configuration data with cache invalidation
 */

interface FormConfigContextType {
  fieldConfigs: FieldConfiguration[];
  applicationTypes: ApplicationType[];
  priorities: ApplicationPriority[];
  statuses: ApplicationStatus[];
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
  ensureLoaded: () => Promise<void>;
  isFieldEnabled: (fieldName: string) => boolean;
  isFieldRequired: (fieldName: string) => boolean;
}

const FormConfigContext = createContext<FormConfigContextType | undefined>(undefined);

interface FormConfigProviderProps {
  children: ReactNode;
}

export const FormConfigProvider: React.FC<FormConfigProviderProps> = ({ children }) => {
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfiguration[]>([]);
  const [applicationTypes, setApplicationTypes] = useState<ApplicationType[]>([]);
  const [priorities, setPriorities] = useState<ApplicationPriority[]>([]);
  const [statuses, setStatuses] = useState<ApplicationStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all configuration data in parallel
      const [fields, types, priors, stats] = await Promise.all([
        getFieldConfigurations(),
        getActiveApplicationTypes(),
        getActivePriorities(),
        getActiveStatuses()
      ]);

      setFieldConfigs(fields);
      setApplicationTypes(types);
      setPriorities(priors);
      setStatuses(stats);
      loadedRef.current = true;
    } catch (err: any) {
      console.error('Error fetching form configuration:', err);
      setError(err.message || 'Failed to load form configuration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh configuration - invalidates cache and re-fetches all data
   * Call this after creating/updating/deleting types, priorities, or statuses
   */
  const refreshConfig = async () => {
    console.log('🔄 Refreshing form configuration...');
    await fetchConfigs();
    console.log('✅ Form configuration refreshed');
  };

  /**
   * Lazy loader — call from pages that need form config (e.g. CreateTransactionPage).
   * No-op if already loaded.
   */
  const ensureLoaded = useCallback(async () => {
    if (loadedRef.current || loading) return;
    const token = localStorage.getItem('access_token');
    if (token) {
      await fetchConfigs();
    }
  }, [loading]);

  // Helper function to check if a field is enabled
  const isFieldEnabled = (fieldName: string): boolean => {
    const config = fieldConfigs.find(c => c.field_name === fieldName);
    return config ? config.is_enabled : true;
  };

  // Helper function to check if a field is required
  const isFieldRequired = (fieldName: string): boolean => {
    const config = fieldConfigs.find(c => c.field_name === fieldName);
    return config ? config.is_required : true;
  };

  // Listen for login event to reset loaded state so next ensureLoaded() re-fetches
  useEffect(() => {
    const handleLoginEvent = () => {
      loadedRef.current = false;
      fetchConfigs();
    };

    window.addEventListener('user-logged-in', handleLoginEvent);
    return () => window.removeEventListener('user-logged-in', handleLoginEvent);
  }, []);

  const value: FormConfigContextType = {
    fieldConfigs,
    applicationTypes,
    priorities,
    statuses,
    loading,
    error,
    refreshConfig,
    ensureLoaded,
    isFieldEnabled,
    isFieldRequired
  };

  return (
    <FormConfigContext.Provider value={value}>
      {children}
    </FormConfigContext.Provider>
  );
};

/**
 * Custom hook to access form configuration context
 * @throws Error if used outside of FormConfigProvider
 */
export const useFormConfigContext = () => {
  const context = useContext(FormConfigContext);
  if (context === undefined) {
    throw new Error('useFormConfigContext must be used within a FormConfigProvider');
  }
  return context;
};

export default FormConfigContext;
